import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { Badge } from "../../components/badge";
import { Button } from "../../components/button";
import { Card, CardContent, CardHeader } from "../../components/card";
import { CommandBar } from "../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { DateTimeLabel } from "../../components/datetime-label";
import { Dropdown, DropdownDivider, DropdownItem } from "../../components/dropdown";
import { FormControl, TextInput } from "../../components/form";
import { Icon } from "../../components/icon";
import { setManagedInterval } from "../../interval-manager";
import { Section } from "../../components/layout";
import { SegmentBar } from "../../components/progress-bar";
import { TabControl } from "../../components/tabcontrol";
import { DefaultRow, Table, TableActionCell, TableBody, TableCell, TableHeader, TableHeaderActionCell, TableHeaderCell, TableRow } from "../../components/table";
import { CopiableText, Heading, MutedText } from "../../components/text";
import { displayFormControl } from "../../ui/alert";
import { formatDuration, formatNumber, formatVeryPreciseCurrency } from "../../ui/format";
import { throttledCall } from "../../ui/throttle";
import { runOperation } from "../../ui/operation";
import { SpecializedControl } from "../../components/specializedObject";
import { confirmDanger, runtimeLabel, StateBadge } from "./shared";
import { ModelBadge } from "../../components/model-icon";

export default function () {
    const workflows = el.state.create([]);
    const jobs = el.state.create([]);
    const mediaJobs = el.state.create(null);
    const workflowFilter = el.state.create("");
    const jobFilter = el.state.create("");

    async function refreshMediaJobs() {
        const response = await api.getJson("/api/v1/compound/logs.json");
        mediaJobs.value = response?.data || [];
    }

    async function refresh() {
        const workflowQuery = new URLSearchParams();
        const jobQuery = new URLSearchParams();
        if (workflowFilter.value)
            workflowQuery.set("filter", workflowFilter.value);
        if (jobFilter.value)
            jobQuery.set("filter", jobFilter.value);

        const [workflowResponse, jobResponse] = await Promise.all([
            api.getJson(`/api/v1/batch/workflows?${workflowQuery.toString()}`),
            api.getJson(`/api/v1/batch/jobs?${jobQuery.toString()}`)
        ]);

        workflows.value = workflowResponse.data || [];
        jobs.value = jobResponse.data || [];
    }

    runOperation("Loading batch data...", async () => {
        await refresh();
        await refreshMediaJobs();
    });
    setManagedInterval(refreshMediaJobs, 5000);

    return Dashboard(
        DashboardTitle("Batch", "Run repeatable AI workflows over queued line-delimited items."),
        TabControl([
            {
                title: "Workflows",
                icon: Icon("flow-chart"),
                contents: el.fragment(
                    Section(
                        Heading(
                            "Workflows",
                            "Workflow settings define the model, processing instruction, output schema, tools, validation and error handling used by future job items."
                        )
                    ),
                    Section(
                        CommandBar(
                            Button(Icon("add-line"), "Create workflow", {
                                onClick() {
                                    page("/dashboard/batch/workflows/create");
                                }
                            }),
                            FormControl(null,
                                TextInput("batchWorkflowFilter", "Filter workflows...", {
                                    onInput(event) {
                                        throttledCall(async () => {
                                            workflowFilter.value = event.target.value;
                                            await refresh();
                                        }, 250);
                                    }
                                })
                            )
                        )
                    ),
                    Section(
                        Card(
                            { class: "bg-card" },
                            CardHeader("Workflow list"),
                            CardContent(
                                Table(
                                    TableHeader(
                                        TableHeaderCell("Id"),
                                        TableHeaderCell("Created"),
                                        TableHeaderCell("Title"),
                                        TableHeaderCell("Model"),
                                        TableHeaderCell("Jobs"),
                                        TableHeaderActionCell("Actions")
                                    ),
                                    el.state.listening([workflows], () =>
                                        TableBody(
                                            workflows.value
                                                .map(item =>
                                                    TableRow(
                                                        TableCell(CopiableText(item.id ? "#" + item.id.slice(-6) : "-", item.id)),
                                                        TableCell(DateTimeLabel(item.createdAt)),
                                                        TableCell(item.title || MutedText("Untitled workflow")),
                                                        TableCell(ModelBadge(item.modelName)),
                                                        TableCell(item.jobCount),
                                                        TableActionCell(
                                                            Dropdown(null,
                                                                DropdownItem("Create job", Icon("play-list-add-line"), {
                                                                    onClick() {
                                                                        displayFormControl({
                                                                            title: "Create batch job",
                                                                            message: "Name this processing run before importing items.",
                                                                            formControlItem: SpecializedControl({
                                                                                fields: [
                                                                                    {
                                                                                        label: "Job name",
                                                                                        name: "title",
                                                                                        control: TextInput(null, "May imports, customer enrichment, lead scoring...")
                                                                                    }
                                                                                ]
                                                                            }),
                                                                            onConfirm(value) {
                                                                                runOperation("Creating batch job...", async () => {
                                                                                    const response = await api.postJson(`/api/v1/batch/workflows/${item.id}/jobs`, {
                                                                                        title: value.title?.nullWhenEmpty()
                                                                                    });
                                                                                    page(`/dashboard/batch/workflows/${item.id}/jobs/${response.data.id}`);
                                                                                });
                                                                            },
                                                                            validate: []
                                                                        });
                                                                    }
                                                                }),
                                                                DropdownItem("Edit", Icon("pencil-line"), {
                                                                    onClick() {
                                                                        page(`/dashboard/batch/workflows/${item.id}`);
                                                                    }
                                                                }),
                                                                DropdownDivider(),
                                                                DropdownItem("Delete", Icon("delete-bin-line"), {
                                                                    onClick() {
                                                                        confirmDanger({
                                                                            title: "Delete workflow",
                                                                            message: "This deletes the workflow, all its jobs and every queued or processed item. This action is destructive and cannot be undone.",
                                                                            buttonText: "Delete workflow",
                                                                            operationText: "Deleting workflow...",
                                                                            async onConfirm() {
                                                                                await api.deleteJson(`/api/v1/batch/workflows/${item.id}`);
                                                                                await refresh();
                                                                            }
                                                                        });
                                                                    }
                                                                })
                                                            )
                                                        )
                                                    )
                                                )
                                                .nullWhenEmpty() || DefaultRow("No workflows found.")
                                        )
                                    )
                                )
                            )
                        )
                    )
                )
            },
            {
                title: "Jobs",
                icon: Icon("list-check-3"),
                contents: el.fragment(
                    Section(
                        Heading(
                            "Jobs",
                            "Jobs are concrete processing runs created from a workflow. Import items, start processing, monitor cost and export successful results from the job page."
                        )
                    ),
                    Section(
                        CommandBar(
                            FormControl(null,
                                TextInput("batchJobFilter", "Filter jobs...", {
                                    onInput(event) {
                                        throttledCall(async () => {
                                            jobFilter.value = event.target.value;
                                            await refresh();
                                        }, 250);
                                    }
                                })
                            )
                        )
                    ),
                    Section(
                        Card(
                            { class: "bg-card" },
                            CardHeader("Job list"),
                            CardContent(
                                Table(
                                    TableHeader(
                                        TableHeaderCell("Id"),
                                        TableHeaderCell("Name"),
                                        TableHeaderCell("State"),
                                        TableHeaderCell("Progress"),
                                        TableHeaderCell("Runtime"),
                                        TableHeaderCell("Events"),
                                        TableHeaderActionCell("Actions")
                                    ),
                                    el.state.listening([jobs], () =>
                                        TableBody(
                                            jobs.value
                                                .map(item =>
                                                    TableRow(
                                                        TableCell(CopiableText(item.id ? "#" + item.id.slice(-6) : "-", item.id)),
                                                        TableCell(
                                                            item.title || MutedText("Untitled job"),
                                                            item.workflowTitle && el("div", MutedText(item.workflowTitle))
                                                        ),
                                                        TableCell(
                                                            StateBadge(item.state)
                                                        ),
                                                        TableCell(
                                                            SegmentBar({
                                                                autosize: true,
                                                                compact: true,
                                                                segments: [
                                                                    { label: "Pending", value: item.pending || 0, color: "#8c959f" },
                                                                    { label: "Running", value: item.running || 0, color: "#0969da" },
                                                                    { label: "Errors", value: (item.refused || 0) + (item.executionErrors || 0) + (item.validationErrors || 0) + (item.cancelled || 0), color: "#cf222e" },
                                                                    { label: "Successful", value: item.finished || 0, color: "#1a7f37" }
                                                                ]
                                                            })
                                                        ),
                                                        TableCell(runtimeLabel(item)),
                                                        TableCell(item.eventCount || 0),
                                                        TableActionCell(
                                                            Button(Icon("settings-3-line"), "Manage", {
                                                                onClick() {
                                                                    page(`/dashboard/batch/workflows/${item.parentBatchWorkflowId}/jobs/${item.id}`);
                                                                }
                                                            })
                                                        )
                                                    )
                                                )
                                                .nullWhenEmpty() || DefaultRow("No batch jobs found.")
                                        )
                                    )
                                )
                            )
                        )
                    )
                )
            },
            {
                title: "Media Processing",
                icon: Icon("file-search-line"),
                contents: el.fragment(
                    Section(
                        Heading(
                            "Media Processing",
                            "Monitor files being processed into RAG documents, their runtime and processing costs."
                        )
                    ),
                    Section(
                        CommandBar(
                            Button(Icon("stop-circle-line"), "Cancel all", {
                                onClick() {
                                    confirmDanger({
                                        message: "This cancels every queued or running media processing job. This action cannot be undone.",
                                        buttonText: "Cancel all jobs",
                                        operationText: "Cancelling media processing jobs...",
                                        async onConfirm() {
                                            await api.deleteJson("/api/v1/compound/jobs");
                                            await refreshMediaJobs();
                                        }
                                    });
                                }
                            })
                        )
                    ),
                    Section(
                        Card(
                            { class: "bg-card" },
                            CardHeader("Media processing jobs"),
                            CardContent(
                                Table(
                                    TableHeader(
                                        TableHeaderCell("Collection"),
                                        TableHeaderCell("Files"),
                                        TableHeaderCell("State"),
                                        TableHeaderCell("Elapsed"),
                                        TableHeaderCell("RAG documents"),
                                        TableHeaderCell("Current cost"),
                                        TableHeaderCell("Estimated cost"),
                                        TableHeaderActionCell("Actions")
                                    ),
                                    el.state.listening([mediaJobs], () =>
                                        TableBody(
                                            mediaJobs.value == null
                                                ? DefaultRow("Loading media processing jobs...")
                                                : mediaJobs.value
                                                    .map(item => {
                                                        const files = Array.isArray(item.files)
                                                            ? item.files
                                                            : [item.files || item.fileName].filter(Boolean);
                                                        const mediaState = String(item.state || "");
                                                        const mediaStateLabel = mediaState
                                                            ? mediaState[0].toUpperCase() + mediaState.slice(1).toLowerCase()
                                                            : "-";
                                                        const normalizedMediaState = mediaState.toLowerCase();
                                                        const canCancel = ["queued", "processing"].includes(normalizedMediaState);
                                                        const canRetryOrDelete = ["failed", "cancelled"].includes(normalizedMediaState);
                                                        return TableRow(
                                                            TableCell(
                                                                item.collection?.name || MutedText("Unknown collection"),
                                                                item.collection?.id && el("div", CopiableText("#" + item.collection.id.slice(-6), item.collection.id))
                                                            ),
                                                            TableCell(
                                                                files.map(file => el("div", typeof file === "string" ? file : file.fileName || file.name || "-"))
                                                            ),
                                                            TableCell(StateBadge(mediaStateLabel)),
                                                            TableCell(item.elapsedSeconds == null ? MutedText("-") : formatDuration(item.elapsedSeconds)),
                                                            TableCell(formatNumber(item.producedDocuments || 0)),
                                                            TableCell(item.currentCost == null ? MutedText("-") : formatVeryPreciseCurrency(item.currentCost)),
                                                            TableCell(item.estimatedCost == null ? MutedText("Unavailable") : formatVeryPreciseCurrency(item.estimatedCost)),
                                                            TableActionCell(
                                                                canRetryOrDelete
                                                                    ? Dropdown(
                                                                        Icon("more-2-line"),
                                                                        DropdownItem("Retry", Icon("restart-line"), {
                                                                            async onClick() {
                                                                                await runOperation("Retrying media processing job...", async () => {
                                                                                    await api.postJson(`/api/v1/compound/jobs/${item.id}/retry`, {});
                                                                                    await refreshMediaJobs();
                                                                                });
                                                                            }
                                                                        }),
                                                                        DropdownItem("Delete", Icon("delete-bin-7-line"), {
                                                                            onClick() {
                                                                                const fileLabel = files.length === 1
                                                                                    ? files[0]?.fileName || files[0]?.name || files[0]
                                                                                    : `${files.length} files`;
                                                                                confirmDanger({
                                                                                    message: `This permanently deletes the processing job for ${fileLabel}. RAG documents already produced are preserved.`,
                                                                                    buttonText: "Delete job",
                                                                                    operationText: "Deleting media processing job...",
                                                                                    async onConfirm() {
                                                                                        await api.deleteJson(`/api/v1/compound/jobs/${item.id}/failed`);
                                                                                        await refreshMediaJobs();
                                                                                    }
                                                                                });
                                                                            }
                                                                        })
                                                                    )
                                                                    : canCancel
                                                                        ? Button(Icon("stop-circle-line"), "Cancel", {
                                                                            onClick() {
                                                                                const fileLabel = files.length === 1
                                                                                    ? files[0]?.fileName || files[0]?.name || files[0]
                                                                                    : `${files.length} files`;
                                                                                confirmDanger({
                                                                                    message: `This cancels processing for ${fileLabel}. This action cannot be undone.`,
                                                                                    buttonText: "Cancel job",
                                                                                    operationText: "Cancelling media processing job...",
                                                                                    async onConfirm() {
                                                                                        await api.deleteJson(`/api/v1/compound/jobs/${item.id}`);
                                                                                        await refreshMediaJobs();
                                                                                    }
                                                                                });
                                                                            }
                                                                        })
                                                                        : MutedText("-")
                                                            )
                                                        );
                                                    })
                                                    .nullWhenEmpty() || DefaultRow("No media processing jobs found.")
                                        )
                                    )
                                )
                            )
                        )
                    )
                )
            }
        ], {
            type: "fixed"
        })
    );
}
